/**
 * Section Header
 * Handles mobile menu toggle, desktop dropdowns, and mobile slide-in submenus
 */

(function() {
  'use strict';

  class SectionHeader {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      
      // Mobile menu elements
      this.mobileMenu = section.querySelector('[data-mobile-menu]');
      this.overlay = section.querySelector('[data-header-overlay]');
      this.menuToggle = section.querySelector('.js-mobile-menu-toggle');
      this.mobileNav = section.querySelector('[data-mobile-nav]');
      
      // Mobile panels
      this.mainPanel = section.querySelector('[data-mobile-panel="main"]');
      this.submenuPanel = section.querySelector('[data-mobile-panel="submenu"]');
      this.submenuContent = section.querySelector('[data-mobile-submenu-content]');
      this.submenuTitle = section.querySelector('[data-mobile-submenu-title]');
      this.submenuCta = section.querySelector('[data-mobile-submenu-cta]');
      
      // Search elements
      this.searchWrapperMobile = section.querySelector('[data-header-search-mobile]');
      this.searchWrapperDesktop = section.querySelector('[data-header-search-desktop]');
      this.searchToggle = section.querySelector('.js-header-search-toggle');
      this.searchNav = section.querySelector('[data-header-nav]');
      this.searchInputMobile = this.searchWrapperMobile?.querySelector('.search__input');
      this.searchInputDesktop = this.searchWrapperDesktop?.querySelector('.search__input');
      this.searchCloseBtnMobile = this.searchWrapperMobile?.querySelector('.btn--close-search');
      this.searchCloseBtnDesktop = this.searchWrapperDesktop?.querySelector('.btn--close-search');
      
      // Desktop dropdown elements (js- prefixed classes for theme blocks)
      this.dropdownWrappers = section.querySelectorAll('.js-header-dropdown');
      
      // Check for independent mobile menu section
      this.mobileMenuSection = document.querySelector('[data-section-type="mobile-menu"]');
      this.useIndependentMobileMenu = !!this.mobileMenuSection;
      
      this.isOpen = false;
      this.isSearchOpen = false;
      this.activeDropdown = null;
      this.isMouseFocus = false; // Track if focus came from mouse vs keyboard
      
      this.init();
    }

    init() {
      this.setHeaderHeight();
      this.setupMobileMenu();
      this.bindEvents();
    }

    /**
     * Setup mobile menu - either use independent section or build from desktop nav
     */
    setupMobileMenu() {
      if (this.useIndependentMobileMenu) {
        this.setupIndependentMobileMenu();
      } else {
        this.buildMobileNav();
      }
    }

    /**
     * Setup independent mobile menu section
     * Extracts content from the mobile menu section's blocks and places them
     * into the header's mobile menu container. Uses the same block types as
     * desktop nav (_section-header-dropdown-link, _section-header-link).
     */
    setupIndependentMobileMenu() {
      if (!this.mobileMenuSection || !this.mobileNav) return;
      
      // Clear existing nav content
      this.mobileNav.innerHTML = '';
      
      // Create menu list
      const menuList = document.createElement('ul');
      menuList.className = 'header__mobile-menu-list';
      menuList.setAttribute('role', 'list');
      
      // Find all dropdown wrappers and links from the mobile menu section
      const dropdownWrappers = this.mobileMenuSection.querySelectorAll('.header-dropdown__wrapper');
      const linkElements = this.mobileMenuSection.querySelectorAll('.header-link');
      
      // Get all navigation items in order
      const allItems = this.mobileMenuSection.querySelectorAll('.header-dropdown__wrapper, .header-link');
      
      // Track dropdown index separately (for matching with mobileMenuDropdowns array)
      let dropdownIndex = 0;
      
      allItems.forEach((item) => {
        const menuItem = document.createElement('li');
        menuItem.className = 'header__mobile-menu-item';
        
        if (item.classList.contains('header-dropdown__wrapper')) {
          // It's a dropdown - create a trigger button
          const trigger = item.querySelector('.js-header-dropdown-trigger');
          if (!trigger) return;
          
          const title = trigger.textContent.trim();
          const menuTrigger = document.createElement('button');
          menuTrigger.className = 'header__mobile-menu-trigger';
          menuTrigger.setAttribute('type', 'button');
          menuTrigger.setAttribute('data-mobile-dropdown-trigger', '');
          menuTrigger.setAttribute('data-dropdown-index', dropdownIndex);
          menuTrigger.innerHTML = `
            <span>${title}</span>
            <svg width="22" height="27" viewBox="0 0 22 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.75 14.5625L12.25 19.8125C11.9375 20.0938 11.4688 20.0938 11.1875 19.7812C10.9062 19.4688 10.9062 19 11.2188 18.7188L15.375 14.75H4.75C4.3125 14.75 4 14.4375 4 14C4 13.5938 4.3125 13.25 4.75 13.25H15.375L11.2188 9.3125C10.9062 9.03125 10.9062 8.53125 11.1875 8.25C11.4688 7.9375 11.9688 7.9375 12.25 8.21875L17.75 13.4688C17.9062 13.625 18 13.8125 18 14C18 14.2188 17.9062 14.4062 17.75 14.5625Z" fill="currentColor"/>
            </svg>
          `;
          menuItem.appendChild(menuTrigger);
          dropdownIndex++;
        } else if (item.classList.contains('header-link')) {
          // It's a simple link
          const link = item.cloneNode(true);
          link.className = 'header__mobile-menu-link';
          menuItem.appendChild(link);
        }
        
        menuList.appendChild(menuItem);
      });
      
      this.mobileNav.appendChild(menuList);
      
      // Append footer links if present
      const footerLinks = this.mobileMenuSection.querySelector('[data-mobile-menu-footer]');
      if (footerLinks && this.mainPanel) {
        // Check if footer already exists in main panel
        const existingFooter = this.mainPanel.querySelector('.header__mobile-menu-footer');
        if (existingFooter) {
          existingFooter.remove();
        }
        const footerClone = footerLinks.cloneNode(true);
        footerClone.style.display = '';
        this.mainPanel.appendChild(footerClone);
      }
      
      // Store dropdown wrappers from the mobile menu section for submenu handling
      this.mobileMenuDropdowns = Array.from(dropdownWrappers);
    }

    setHeaderHeight() {
      const headerWrapper = this.section.querySelector('.header__wrapper');
      if (headerWrapper) {
        const height = headerWrapper.offsetHeight;
        this.section.style.setProperty('--header-height', `${height}px`);
        
        // Calculate offset from viewport top to bottom of header wrapper
        // For fixed position menu, we need viewport-relative position, not document position
        const headerRect = headerWrapper.getBoundingClientRect();
        const headerBottom = headerRect.bottom;
        this.section.style.setProperty('--header-offset-top', `${headerBottom}px`);
      }
    }

    buildMobileNav() {
      if (!this.mobileNav) return;
      
      // Clear existing content
      this.mobileNav.innerHTML = '';
      
      // Create mobile menu list
      const menuList = document.createElement('ul');
      menuList.className = 'header__mobile-menu-list';
      menuList.setAttribute('role', 'list');
      
      // Get all dropdown wrappers and create mobile menu items
      this.dropdownWrappers.forEach((wrapper, index) => {
        const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
        const dropdownMenu = wrapper.querySelector('.js-header-dropdown-menu');
        
        if (!trigger) return;
        
        const title = trigger.textContent.trim();
        const menuItem = document.createElement('li');
        menuItem.className = 'header__mobile-menu-item';
        
        const menuTrigger = document.createElement('button');
        menuTrigger.className = 'header__mobile-menu-trigger';
        menuTrigger.setAttribute('type', 'button');
        menuTrigger.setAttribute('data-mobile-dropdown-trigger', '');
        menuTrigger.setAttribute('data-dropdown-index', index);
        menuTrigger.innerHTML = `
          <span>${title}</span>
          <svg width="22" height="27" viewBox="0 0 22 27" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.75 14.5625L12.25 19.8125C11.9375 20.0938 11.4688 20.0938 11.1875 19.7812C10.9062 19.4688 10.9062 19 11.2188 18.7188L15.375 14.75H4.75C4.3125 14.75 4 14.4375 4 14C4 13.5938 4.3125 13.25 4.75 13.25H15.375L11.2188 9.3125C10.9062 9.03125 10.9062 8.53125 11.1875 8.25C11.4688 7.9375 11.9688 7.9375 12.25 8.21875L17.75 13.4688C17.9062 13.625 18 13.8125 18 14C18 14.2188 17.9062 14.4062 17.75 14.5625Z" fill="currentColor"/>
          </svg>

        `;
        
        menuItem.appendChild(menuTrigger);
        menuList.appendChild(menuItem);
      });
      
      this.mobileNav.appendChild(menuList);
    }

    bindEvents() {
      // Search toggle button
      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', () => {
          if (this.isSearchOpen) {
            this.closeSearch();
          } else {
            this.openSearch();
          }
        });
      }

      // Close search button inside search bar
      if (this.searchCloseBtn) {
        this.searchCloseBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeSearch();
        });
      }

      // Mobile menu toggle - now acts as both open and close
      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => {
          if (this.isOpen) {
            this.closeMenu();
          } else {
            this.openMenu();
          }
        });
      }
      
      // Close buttons (in submenu panel)
      this.section.querySelectorAll('.js-mobile-menu-close').forEach(closeBtn => {
        closeBtn.addEventListener('click', this.closeMenu.bind(this));
      });
      
      // Back button for submenu
      const backBtn = this.section.querySelector('.js-mobile-menu-back');
      if (backBtn) {
        backBtn.addEventListener('click', this.closeSubmenu.bind(this));
      }
      
      if (this.overlay) {
        this.overlay.addEventListener('click', this.closeMenu.bind(this));
      }

      // Mobile menu item triggers - open submenu
      if (this.mobileNav) {
        this.mobileNav.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-mobile-dropdown-trigger]');
          if (trigger) {
            e.preventDefault();
            const index = parseInt(trigger.dataset.dropdownIndex, 10);
            if (this.useIndependentMobileMenu) {
              this.openSubmenuById(index);
            } else {
              this.openSubmenu(index);
            }
          }
        });
      }

      // Desktop dropdowns - click and focus handlers
      this.dropdownWrappers.forEach(wrapper => {
        const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
        if (trigger) {
          // Track if focus came from keyboard (Tab) vs mouse click
          trigger.addEventListener('mousedown', () => {
            this.isMouseFocus = true;
          });
          
          // Click to toggle
          trigger.addEventListener('click', (e) => {
            this.toggleDropdown(e, wrapper);
            // Reset mouse focus flag after click
            this.isMouseFocus = false;
          });
          
          // Focus to open (for keyboard navigation only)
          trigger.addEventListener('focus', () => {
            // Only open on focus if it wasn't from a mouse click
            if (!this.isMouseFocus) {
              this.openDropdownOnFocus(wrapper);
            }
            this.isMouseFocus = false;
          });
        }
        
        // Handle keyboard navigation within dropdown
        wrapper.addEventListener('keydown', (e) => this.handleDropdownKeydown(e, wrapper));
      });

      // Close dropdowns when focus leaves the dropdown entirely
      this.section.addEventListener('focusout', (e) => {
        // Delay to allow new focus to be set
        setTimeout(() => {
          if (this.activeDropdown) {
            const focusedElement = document.activeElement;
            const isStillInDropdown = this.activeDropdown.contains(focusedElement);
            const isInHeader = this.section.contains(focusedElement);
            
            if (!isStillInDropdown && isInHeader) {
              // Focus moved to another header element, close dropdown
              this.closeAllDropdowns();
            } else if (!isInHeader) {
              // Focus left header entirely
              this.closeAllDropdowns();
            }
          }
        }, 0);
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (this.activeDropdown && !e.target.closest('.js-header-dropdown')) {
          this.closeAllDropdowns();
        }
        
        // Close search when clicking outside
        if (this.isSearchOpen) {
          const clickedInsideSearch = e.target.closest('[data-header-search-mobile], [data-header-search-desktop]');
          const clickedSearchToggle = e.target.closest('.js-header-search-toggle');
          
          if (!clickedInsideSearch && !clickedSearchToggle) {
            this.closeSearch();
          }
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (this.isSearchOpen) {
            this.closeSearch();
          }
          if (this.activeDropdown) {
            this.closeAllDropdowns();
          }
          if (this.isOpen) {
            this.closeMenu();
          }
        }
      });

      // Resize handler - recalculate header height and close menu on desktop
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.setHeaderHeight();
          
          // Close mobile menu when resizing to desktop
          if (window.innerWidth >= 1100 && this.isOpen) {
            this.closeMenu();
          }
          
          // Sync search state when resizing between mobile/desktop
          if (this.isSearchOpen) {
            const isMobile = window.innerWidth < 1100;
            const mobileActive = this.searchWrapperMobile?.classList.contains('is-active');
            const desktopActive = this.searchWrapperDesktop?.classList.contains('is-active');
            
            // Close the inactive viewport's search and open the active one
            if (isMobile && desktopActive) {
              // Switched to mobile - close desktop, open mobile
              this.searchWrapperDesktop?.classList.remove('is-active', 'is-visible');
              this.searchWrapperDesktop?.setAttribute('aria-hidden', 'true');
              
              if (this.searchWrapperMobile) {
                this.searchWrapperMobile.classList.add('is-active');
                this.searchWrapperMobile.setAttribute('aria-hidden', 'false');
                this.searchWrapperMobile.offsetHeight;
                requestAnimationFrame(() => {
                  this.searchWrapperMobile?.classList.add('is-visible');
                });
              }
            } else if (!isMobile && mobileActive) {
              // Switched to desktop - close mobile, open desktop
              this.searchWrapperMobile?.classList.remove('is-active', 'is-visible');
              this.searchWrapperMobile?.setAttribute('aria-hidden', 'true');
              
              if (this.searchWrapperDesktop) {
                this.searchWrapperDesktop.classList.add('is-active');
                this.searchWrapperDesktop.setAttribute('aria-hidden', 'false');
                this.searchWrapperDesktop.offsetHeight;
                requestAnimationFrame(() => {
                  this.searchWrapperDesktop?.classList.add('is-visible');
                });
              }
            }
          }
        }, 100);
      });
    }

    // Desktop dropdown methods
    toggleDropdown(e, wrapper) {
      e.preventDefault();
      e.stopPropagation();
      
      const dropdown = wrapper.querySelector('.js-header-dropdown-menu');
      const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
      
      if (!dropdown) return;

      const isOpen = wrapper.classList.contains('is-active');

      // Close search if open
      if (this.isSearchOpen) {
        this.closeSearch();
      }
      
      if (isOpen) {
        // Close this dropdown
        this.closeAllDropdowns();
      } else {
        // Close other dropdowns first, then open this one
        this.closeAllDropdowns();
        
        // Show overlay
        if (this.overlay) {
          this.overlay.classList.add('is-visible');
        }

        wrapper.classList.add('is-active');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        if (dropdown) dropdown.setAttribute('aria-hidden', 'false');
        this.activeDropdown = wrapper;
      }
    }

    closeAllDropdowns() {
      this.dropdownWrappers.forEach(wrapper => {
        wrapper.classList.remove('is-active');
        const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
        const dropdown = wrapper.querySelector('.js-header-dropdown-menu');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (dropdown) dropdown.setAttribute('aria-hidden', 'true');
      });
      this.activeDropdown = null;
      this.overlay?.classList.remove('is-visible');
    }

    /**
     * Open dropdown when trigger receives focus (keyboard navigation)
     */
    openDropdownOnFocus(wrapper) {
      const dropdown = wrapper.querySelector('.js-header-dropdown-menu');
      const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
      
      if (!dropdown) return;
      
      const isOpen = wrapper.classList.contains('is-active');
      if (isOpen) return; // Already open
      
      // Close search if open
      if (this.isSearchOpen) {
        this.closeSearch();
      }
      
      // Close other dropdowns
      this.closeAllDropdowns();
      
      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }
      
      wrapper.classList.add('is-active');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (dropdown) dropdown.setAttribute('aria-hidden', 'false');
      this.activeDropdown = wrapper;
    }

    /**
     * Handle keyboard navigation within dropdowns
     */
    handleDropdownKeydown(e, wrapper) {
      const dropdown = wrapper.querySelector('.js-header-dropdown-menu');
      const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
      
      if (!dropdown) return;
      
      const isOpen = wrapper.classList.contains('is-active');
      const focusableElements = dropdown.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      const focusableArray = Array.from(focusableElements);
      const currentIndex = focusableArray.indexOf(document.activeElement);
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (isOpen && focusableArray.length > 0) {
            // Move focus to next item or first if at trigger
            if (document.activeElement === trigger) {
              focusableArray[0]?.focus();
            } else if (currentIndex < focusableArray.length - 1) {
              focusableArray[currentIndex + 1]?.focus();
            }
          }
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen && focusableArray.length > 0) {
            // Move focus to previous item or trigger if at first
            if (currentIndex === 0) {
              trigger?.focus();
            } else if (currentIndex > 0) {
              focusableArray[currentIndex - 1]?.focus();
            }
          }
          break;
          
        case 'ArrowRight':
          // Move to next dropdown trigger
          e.preventDefault();
          this.focusNextDropdown(wrapper);
          break;
          
        case 'ArrowLeft':
          // Move to previous dropdown trigger
          e.preventDefault();
          this.focusPreviousDropdown(wrapper);
          break;
          
        case 'Home':
          e.preventDefault();
          if (isOpen && focusableArray.length > 0) {
            focusableArray[0]?.focus();
          }
          break;
          
        case 'End':
          e.preventDefault();
          if (isOpen && focusableArray.length > 0) {
            focusableArray[focusableArray.length - 1]?.focus();
          }
          break;
          
        case 'Tab':
          // Let Tab work naturally but close dropdown if tabbing out
          if (isOpen) {
            const isLastElement = document.activeElement === focusableArray[focusableArray.length - 1];
            const isFirstElement = document.activeElement === trigger || document.activeElement === focusableArray[0];
            
            if (!e.shiftKey && isLastElement) {
              // Tabbing forward from last element - close and move to next
              this.closeAllDropdowns();
            } else if (e.shiftKey && (document.activeElement === trigger)) {
              // Tabbing backward from trigger - close
              this.closeAllDropdowns();
            }
          }
          break;
      }
    }

    /**
     * Focus the next dropdown trigger
     */
    focusNextDropdown(currentWrapper) {
      const wrappers = Array.from(this.dropdownWrappers);
      const currentIndex = wrappers.indexOf(currentWrapper);
      const nextIndex = (currentIndex + 1) % wrappers.length;
      const nextTrigger = wrappers[nextIndex]?.querySelector('.js-header-dropdown-trigger');
      nextTrigger?.focus();
    }

    /**
     * Focus the previous dropdown trigger
     */
    focusPreviousDropdown(currentWrapper) {
      const wrappers = Array.from(this.dropdownWrappers);
      const currentIndex = wrappers.indexOf(currentWrapper);
      const prevIndex = currentIndex === 0 ? wrappers.length - 1 : currentIndex - 1;
      const prevTrigger = wrappers[prevIndex]?.querySelector('.js-header-dropdown-trigger');
      prevTrigger?.focus();
    }

    // Search methods
    openSearch() {
      const isMobile = window.innerWidth < 1100;
      const searchWrapper = isMobile ? this.searchWrapperMobile : this.searchWrapperDesktop;
      const searchInput = isMobile ? this.searchInputMobile : this.searchInputDesktop;

      if (!searchWrapper) return;

      this.isSearchOpen = true;

      // Close mobile menu if open
      if (this.isOpen) {
        this.closeMenu();
      }

      // Close any open dropdowns first
      this.closeAllDropdowns();

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }

      // Set display to show the search wrapper
      searchWrapper.classList.add('is-active');
      searchWrapper.setAttribute('aria-hidden', 'false');

      // Trigger reflow to ensure display change is applied
      searchWrapper.offsetHeight;

      // Slide in search from top
      requestAnimationFrame(() => {
        searchWrapper.classList.add('is-visible');
      });

      // Update button icons immediately
      if (this.searchToggle) {
        this.searchToggle.setAttribute('aria-expanded', 'true');
        const searchIcon = this.searchToggle.querySelector('.header__search-icon');
        const closeIcon = this.searchToggle.querySelector('.header__close-icon');
        if (searchIcon) searchIcon.style.display = 'none';
        if (closeIcon) closeIcon.style.display = 'block';
      }

      // Focus the search input after animation
      setTimeout(() => {
        if (searchInput) {
          searchInput.focus();
        }
      }, 350);
    }

    closeSearch() {
      const isMobile = window.innerWidth < 1100;
      const searchWrapper = isMobile ? this.searchWrapperMobile : this.searchWrapperDesktop;
      const searchInput = isMobile ? this.searchInputMobile : this.searchInputDesktop;

      if (!searchWrapper) return;

      this.isSearchOpen = false;

      // Hide overlay
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }

      // Slide out search
      searchWrapper.classList.remove('is-visible');

      // Wait for slide out, then hide
      setTimeout(() => {
        searchWrapper.classList.remove('is-active');
        searchWrapper.setAttribute('aria-hidden', 'true');
      }, 300);

      // Update button icons immediately
      if (this.searchToggle) {
        this.searchToggle.setAttribute('aria-expanded', 'false');
        const searchIcon = this.searchToggle.querySelector('.header__search-icon');
        const closeIcon = this.searchToggle.querySelector('.header__close-icon');
        if (searchIcon) searchIcon.style.display = 'block';
        if (closeIcon) closeIcon.style.display = 'none';
      }

      // Clear search input
      if (searchInput) {
        searchInput.value = '';
      }

      // Close predictive search if active
      const predictiveSearch = searchWrapper.querySelector('predictive-search');
      if (predictiveSearch && predictiveSearch.classList.contains('is-active')) {
        document.dispatchEvent(new CustomEvent('predictive-search:close'));
      }
    }

    // Mobile submenu methods
    openSubmenu(index) {
      const wrapper = this.dropdownWrappers[index];
      if (!wrapper || !this.submenuContent || !this.mobileMenu) return;
      
      const dropdownMenu = wrapper.querySelector('.js-header-dropdown-menu');
      const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
      if (!dropdownMenu) return;
      
      // Set the dropdown title
      if (this.submenuTitle && trigger) {
        this.submenuTitle.textContent = trigger.textContent.trim();
      }
      
      // Extract and set CTA if present
      if (this.submenuCta) {
        const ctaElement = dropdownMenu.querySelector('.header-dropdown-cta');
        if (ctaElement) {
          this.submenuCta.innerHTML = ctaElement.outerHTML;
        } else {
          this.submenuCta.innerHTML = '';
        }
      }
      
      // Clone the dropdown content for mobile (excluding footer with CTA)
      const contentClone = dropdownMenu.cloneNode(true);
      // Remove the footer from the cloned content since CTA is now in header
      const footer = contentClone.querySelector('.header-dropdown-menu__footer');
      if (footer) {
        footer.remove();
      }
      this.submenuContent.innerHTML = contentClone.innerHTML;
      
      // Initialize page navigation scrollbars if present
      this.initHeaderPageNavScrollbars();
      
      // Open submenu panel
      this.mobileMenu.classList.add('is-submenu-open');
      if (this.submenuPanel) {
        this.submenuPanel.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Open submenu by dropdown ID (for independent mobile menu section)
     * Uses the same dropdown structure as desktop nav (_section-header-dropdown-link)
     */
    openSubmenuById(dropdownId) {
      if (!this.mobileMenuDropdowns || !this.submenuContent || !this.mobileMenu) return;
      
      // dropdownId is actually the index when using standard dropdown blocks
      const index = parseInt(dropdownId, 10);
      const wrapper = this.mobileMenuDropdowns[index];
      if (!wrapper) return;
      
      const dropdownMenu = wrapper.querySelector('.js-header-dropdown-menu');
      const trigger = wrapper.querySelector('.js-header-dropdown-trigger');
      if (!dropdownMenu) return;
      
      // Set the dropdown title
      if (this.submenuTitle && trigger) {
        this.submenuTitle.textContent = trigger.textContent.trim();
      }
      
      // Extract and set CTA if present
      if (this.submenuCta) {
        const ctaElement = dropdownMenu.querySelector('.header-dropdown-cta');
        if (ctaElement) {
          this.submenuCta.innerHTML = ctaElement.outerHTML;
        } else {
          this.submenuCta.innerHTML = '';
        }
      }
      
      // Clone the dropdown content for mobile (excluding footer with CTA)
      const contentClone = dropdownMenu.cloneNode(true);
      // Remove the footer from the cloned content since CTA is now in header
      const footer = contentClone.querySelector('.header-dropdown-menu__footer');
      if (footer) {
        footer.remove();
      }
      this.submenuContent.innerHTML = contentClone.innerHTML;
      
      // Initialize page navigation scrollbars if present
      this.initHeaderPageNavScrollbars();
      
      // Open submenu panel
      this.mobileMenu.classList.add('is-submenu-open');
      if (this.submenuPanel) {
        this.submenuPanel.setAttribute('aria-hidden', 'false');
      }
    }

    // Initialize page navigation Splide sliders within submenu
    initHeaderPageNavScrollbars() {
      if (!this.submenuContent) return;
      if (typeof Splide === 'undefined') return;
      
      const pageNavs = this.submenuContent.querySelectorAll('[data-header-page-nav]');
      pageNavs.forEach(pageNav => {
        const slider = pageNav.querySelector('[data-header-page-nav-slider]');
        if (!slider) return;
        
        // Initialize Splide with 3 items per page on mobile, 5 on tablet+
        const splideInstance = new Splide(slider, {
          perPage: 5,
          perMove: 1,
          gap: '12px',
          arrows: false,
          pagination: false,
          drag: true,
          snap: true,
          breakpoints: {
            767: {
              perPage: 3,
            }
          },
          i18n: {
            carousel: 'Quick navigation carousel',
            slide: 'navigation item',
            slideLabel: '%s of %s',
          }
        });
        
        splideInstance.mount();
        
        // Setup scrollbar using SplideUtils if available
        if (typeof window.SplideUtils !== 'undefined' && typeof window.SplideUtils.setupScrollbar === 'function') {
          window.SplideUtils.setupScrollbar(splideInstance, pageNav);
        } else {
          // Fallback: manual scrollbar setup
          const scrollbar = pageNav.querySelector('[data-slider-scrollbar]');
          const thumb = scrollbar?.querySelector('[data-slider-scrollbar-thumb]');
          
          if (scrollbar && thumb) {
            const updateScrollbar = () => {
              const perPage = splideInstance.options.perPage || 1;
              const totalSlides = splideInstance.length;
              const currentIndex = splideInstance.index;
              const maxIndex = Math.max(0, totalSlides - perPage);
              
              const thumbWidth = (perPage / totalSlides) * 100;
              thumb.style.width = thumbWidth + '%';
              
              const thumbPosition = maxIndex > 0 ? (currentIndex / maxIndex) * (100 - thumbWidth) : 0;
              thumb.style.left = thumbPosition + '%';
            };
            
            splideInstance.on('mounted move', updateScrollbar);
            updateScrollbar();
          }
        }
        
        // Store instance for cleanup
        slider._splideInstance = splideInstance;
      });
    }

    // Destroy page navigation Splide instances
    destroyHeaderPageNavSliders() {
      if (!this.submenuContent) return;
      
      const sliders = this.submenuContent.querySelectorAll('[data-header-page-nav-slider]');
      sliders.forEach(slider => {
        if (slider._splideInstance) {
          slider._splideInstance.destroy();
          slider._splideInstance = null;
        }
      });
    }

    closeSubmenu() {
      if (!this.mobileMenu) return;
      
      // Destroy Splide instances before clearing content
      this.destroyHeaderPageNavSliders();
      
      this.mobileMenu.classList.remove('is-submenu-open');
      if (this.submenuPanel) {
        this.submenuPanel.setAttribute('aria-hidden', 'true');
      }
      
      // Focus the back button's original trigger or first menu item
      const firstMenuItem = this.mobileNav?.querySelector('.header__mobile-menu-trigger, .header__mobile-menu-link');
      if (firstMenuItem) {
        setTimeout(() => firstMenuItem.focus(), 100);
      }
      
      // Clear content after transition
      setTimeout(() => {
        if (this.submenuContent) {
          this.submenuContent.innerHTML = '';
        }
        if (this.submenuTitle) {
          this.submenuTitle.textContent = '';
        }
        if (this.submenuCta) {
          this.submenuCta.innerHTML = '';
        }
      }, 300);
    }

    // Mobile menu methods
    openMenu() {
      if (!this.mobileMenu) return;
      
      this.isOpen = true;
      this.mobileMenu.classList.add('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }
      
      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'true');
      }

      // Prevent scroll on root element (more reliable on iOS)
      document.documentElement.style.overflow = 'hidden';

      // Setup focus trap
      this.setupMobileMenuFocusTrap();

      // Focus first focusable element in menu after a brief delay
      setTimeout(() => {
        const firstFocusable = this.mobileMenu.querySelector('a, button');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100);
    }

    /**
     * Setup focus trap for mobile menu
     * Keeps focus within the mobile menu when open
     */
    setupMobileMenuFocusTrap() {
      if (this.focusTrapHandler) {
        this.mobileMenu.removeEventListener('keydown', this.focusTrapHandler);
      }
      
      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;
        
        const focusableElements = this.mobileMenu.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements.length === 0) return;
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      };
      
      this.mobileMenu.addEventListener('keydown', this.focusTrapHandler);
    }

    /**
     * Remove focus trap handler
     */
    removeMobileMenuFocusTrap() {
      if (this.focusTrapHandler && this.mobileMenu) {
        this.mobileMenu.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }
    }

    closeMenu() {
      if (!this.mobileMenu) return;
      
      this.isOpen = false;
      this.mobileMenu.classList.remove('is-open');
      this.mobileMenu.classList.remove('is-submenu-open');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      
      if (this.submenuPanel) {
        this.submenuPanel.setAttribute('aria-hidden', 'true');
      }
      
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
      }
      
      // Remove focus trap
      this.removeMobileMenuFocusTrap();
      
      if (this.menuToggle) {
        this.menuToggle.setAttribute('aria-expanded', 'false');
        this.menuToggle.focus();
      }

      // Restore scroll on root element
      document.documentElement.style.overflow = '';
      
      // Clear submenu content after transition
      setTimeout(() => {
        if (this.submenuContent) {
          this.submenuContent.innerHTML = '';
        }
      }, 300);
    }
  }

  // Initialize all header sections
  function init() {
    const headers = document.querySelectorAll('[data-section-type="header"]');
    headers.forEach(header => new SectionHeader(header));
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinitialize on Shopify section events (theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId) {
        const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
        if (section) {
          // Reinitialize if header section is loaded
          if (section.dataset.sectionType === 'header') {
            new SectionHeader(section);
          }
          // Reinitialize header if mobile menu section is loaded (to pick up new content)
          if (section.dataset.sectionType === 'mobile-menu') {
            const headerSection = document.querySelector('[data-section-type="header"]');
            if (headerSection) {
              new SectionHeader(headerSection);
            }
          }
        }
      }
    });
    
    // Also handle block events for mobile menu section
    document.addEventListener('shopify:block:select', function(event) {
      const mobileMenuSection = event.target.closest('[data-section-type="mobile-menu"]');
      if (mobileMenuSection) {
        const headerSection = document.querySelector('[data-section-type="header"]');
        if (headerSection) {
          new SectionHeader(headerSection);
        }
      }
    });
  }
})();
